import { Connection } from 'sharedb/lib/client';
import { Socket } from 'sharedb/lib/sharedb';
import { BaseTypedEmitter } from '../../BaseTypedEmitter';
import { UnknownError, DisconnectedError } from '../Errors';
import { ShareDBSocket } from './ShareDBSocket';

type ShareDBEvents = {
  SDB_CONNECT: () => void;
  SDB_DISCONNECT: (error: DisconnectedError) => void;
  SDB_DESTROYED: () => void;
  SDB_ERROR: (event: Event, error: UnknownError) => void;
};

export default class ShareDB extends BaseTypedEmitter<ShareDBEvents> {
  private config: any;
  private isDestroying: boolean;

  private documentId?: string;
  private token?: string;

  private socket?: Socket;

  private shareDB?: Connection;

  constructor(config: any) {
    super();
    this.config = config;

    this.isDestroying = false;
    this.onclose = this.onclose.bind(this);
  }

  onclose(event: CloseEvent) {
    if (this.isDestroying) {
      this.emit('SDB_DESTROYED');
    } else {
      this.emit(
        'SDB_DISCONNECT',
        new DisconnectedError(event.reason, {
          context: event,
          description: 'ShareDB connection closed',
        }),
      );
    }
  }

  connect(tenant: string, documentId: string, token: string) {
    this.documentId = documentId;
    this.token = token;

    if (this.socket) {
      this.socket.close();
      this.shareDB = undefined;
    }

    this.socket = new ShareDBSocket(
      `${this.config.protos.sharedb}${this.config.rt}${this.config.locations.sharedb}?token=${token}&domain=${tenant}&document=${documentId}`,
    );

    if (this.socket) {
      this.socket.onopen = () => {
        if (this.socket) {
          this.shareDB = new Connection(this.socket);
          this.emit('SDB_CONNECT');
        }
      };

      this.socket.onerror = (event: Event) => {
        this.emit('SDB_ERROR', event, new UnknownError(event));
      };

      this.socket.onclose = this.onclose;
    }
  }

  get connected() {
    return !!(this.socket?.readyState === 1);
  }

  disconnect() {
    if (this.connected) {
      this.socket?.close();
    }
  }

  destroy() {
    this.isDestroying = true;
    if (this.connected) {
      this.socket?.close();
    }
    this.socket = undefined;
    this.shareDB = undefined;
    super.destroy();
  }

  onConnect(callback: ShareDBEvents['SDB_CONNECT']) {
    this.on('SDB_CONNECT', callback);
  }

  onDisconnect(callback: ShareDBEvents['SDB_DISCONNECT']) {
    this.on('SDB_DISCONNECT', callback);
  }

  onDestroy(callback: ShareDBEvents['SDB_DESTROYED']) {
    this.on('SDB_DESTROYED', callback);
  }

  onError(callback: ShareDBEvents['SDB_ERROR']) {
    this.on('SDB_ERROR', callback);
  }

  get<T extends Realtime.Transport.ShareDBCollectionNames>(
    collectionName: T,
    documentID: string,
  ): Realtime.Transport.ShareDBCollectionMapper[T]['model'] {
    if (!this.shareDB) {
      throw new Error('ShareDB is not initialized');
    }

    return this.shareDB.get(collectionName, documentID);
  }

  getPresence(...args: Parameters<Connection['getPresence']>) {
    if (!this.shareDB) {
      throw new Error('ShareDB is not initialized');
    }

    return this.shareDB.getPresence(...args);
  }

  createFetchQuery(...args: Parameters<Connection['createFetchQuery']>) {
    if (!this.shareDB) {
      throw new Error('ShareDB is not initialized');
    }

    return this.shareDB.createFetchQuery(...args);
  }

  createSubscribeQuery(...args: Parameters<Connection['createSubscribeQuery']>) {
    if (!this.shareDB) {
      throw new Error('ShareDB is not initialized');
    }

    return this.shareDB.createSubscribeQuery(...args);
  }

  fetchSnapshot(...args: Parameters<Connection['fetchSnapshot']>) {
    if (!this.shareDB) {
      throw new Error('ShareDB is not initialized');
    }

    return this.shareDB.fetchSnapshot(...args);
  }

  getLastVersionOf(...args: Parameters<Connection['fetchSnapshot']>) {
    if (!this.shareDB) {
      throw new Error('ShareDB is not initialized');
    }

    return this.shareDB.fetchSnapshot(...args);
  }

  fetchSnapshotByTimestamp(...args: Parameters<Connection['fetchSnapshotByTimestamp']>) {
    if (!this.shareDB) {
      throw new Error('ShareDB is not initialized');
    }

    return this.shareDB.fetchSnapshotByTimestamp(...args);
  }
}
